import { useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import SyncContext from './SyncContext';
import { useEventDispatcher } from 'Components/Organisms/EventDispatcherContext';
import { UPDATE_PROVIDER_CONNECTION } from 'Utils/eventTypes';
import {
    getProviderConnection,
    rejectProviderConnection,
    synchronizeProviderConnection,
} from 'Services/providersConnections';
import { PROVIDER_CONNECTION_STATUSES } from 'Utils/constants';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { CONNECTORS_INCIDENTS_QUERY_KEY } from 'Hooks/useConnectorsIncidents';
import { useTranslation } from 'react-i18next';

const DELAY_BETWEEN_REQUESTS = 8000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function SyncProvider({ children }) {
    const { validProvidersConnections: providersConnections } = useProvidersConnections();

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();
    const hasBeenChecked = useRef(false);
    const intervalRef = useRef({});
    const eventDispatcher = useEventDispatcher();
    const { t } = useTranslation('providers');

    const startPolling = useCallback((providerConnectionId, callback) => {
        // Overwriting a running interval would leave it polling, and showing its prompts again, forever.
        clearInterval(intervalRef.current[providerConnectionId] ?? null);
        intervalRef.current[providerConnectionId] = setInterval(callback, DELAY_BETWEEN_REQUESTS);
    }, []);

    const cancelSynchronization = useCallback(
        (providerConnection) => {
            rejectProviderConnection(providerConnection.id)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['providers-connections'] });
                    enqueueSnackbar(
                        t('sync-cancelled', {
                            name: providerConnection.name,
                            interpolation: { escapeValue: false },
                        }),
                        { variant: 'success' },
                    );
                })
                .catch((error) => showUnknownError(error));
        },
        [enqueueSnackbar, queryClient, showUnknownError, t],
    );

    const startSynchronization = useCallback(
        ({
            providerConnection,
            isCompleteSync = false,
            otp = null,
            session_id = null,
            counter_id = null,
            remove_deleted = false,
            product_id,
        } = {}) => {
            enqueueSnackbar(providerConnection.name, {
                variant: 'providerConnectionSynchronize',
                key: `${providerConnection.id}-synchronizing`,
                persist: true,
                icon: providerConnection.logo_url,
            });

            synchronizeProviderConnection({
                id: providerConnection.id,
                isCompleteSync,
                otp,
                session_id,
                counter_id,
                product_id,
                remove_deleted,
            })
                .then(() => {
                    eventDispatcher.dispatch(UPDATE_PROVIDER_CONNECTION, {
                        ...providerConnection,
                        status: PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING,
                    });
                    startPolling(providerConnection.id, () =>
                        checkSynchronization({ providerConnection, isCompleteSync, product_id }),
                    );
                })
                .catch((errorResponse) => {
                    if (
                        errorResponse.response?.status === 400 &&
                        errorResponse.response?.data?.description === 'invalid_synchronization_status'
                    ) {
                        enqueueSnackbar(providerConnection.name, {
                            variant: 'providerConnectionError',
                            key: `${providerConnection.id}-error`,
                        });
                    }

                    if (
                        errorResponse.response?.status === 400 &&
                        errorResponse.response?.data?.description === 'connector_broken'
                    ) {
                        // The bank went down after the page was loaded: refresh what the app knows.
                        closeSnackbar(`${providerConnection.id}-synchronizing`);
                        queryClient.invalidateQueries({ queryKey: CONNECTORS_INCIDENTS_QUERY_KEY });
                        showUnknownError(errorResponse);
                    }
                });
        },
        [enqueueSnackbar, closeSnackbar, showUnknownError, queryClient, startPolling],
    );

    const promptVerification = useCallback(
        ({ providerConnection, statusMessage, ...syncParams }) => {
            const actionParams = {
                ...syncParams,
                providerConnection,
                otp: null,
                session_id: statusMessage?.info?.session_id,
                counter_id: statusMessage?.info?.counter_id,
            };
            const onCancel = () => cancelSynchronization(providerConnection);

            if (statusMessage?.description === 'requires_signature') {
                enqueueSnackbar(providerConnection.name, {
                    variant: 'providerConnectionVerify',
                    action: () => startSynchronization(actionParams),
                    onCancel,
                    icon: providerConnection.logo_url,
                    persist: true,
                    key: `${providerConnection.id}-verify`,
                });
                return;
            }

            const message = statusMessage?.info?.service_message || 'Introduce el código que te ha enviado tu banco.';
            enqueueSnackbar(message, {
                variant: 'providerConnectionRequestOtp',
                persist: true,
                action: ({ otp }) => startSynchronization({ ...actionParams, otp }),
                onCancel,
                icon: providerConnection.logo_url,
                key: `${providerConnection.id}-request-otp`,
            });
        },
        [enqueueSnackbar, cancelSynchronization, startSynchronization],
    );

    const checkSynchronization = useCallback(
        (params) => {
            const { providerConnection } = params;

            getProviderConnection(providerConnection.id)
                .then(async (providerConnectionUpdated) => {
                    if (providerConnectionUpdated?.status !== PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING) {
                        closeSnackbar(`${providerConnection.id}-synchronizing`);
                        clearInterval(intervalRef.current[providerConnection.id] ?? null);
                        await wait(500);
                    }

                    if (providerConnectionUpdated?.status === PROVIDER_CONNECTION_STATUSES.OK) {
                        eventDispatcher.dispatch(UPDATE_PROVIDER_CONNECTION, providerConnectionUpdated);
                        enqueueSnackbar(providerConnection.name, {
                            variant: 'providerConnectionSuccess',
                            key: `${providerConnection.id}-success`,
                        });
                    } else if (
                        providerConnectionUpdated?.status === PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR_OTP
                    ) {
                        promptVerification({ ...params, statusMessage: providerConnectionUpdated.status_message });
                    } else if (providerConnectionUpdated?.status !== PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING) {
                        enqueueSnackbar(providerConnection.name, { variant: 'providerConnectionError' });
                    }
                })
                .catch((errorResponse) => {
                    clearInterval(intervalRef.current[providerConnection.id] ?? null);
                    if (
                        errorResponse.response?.status === 400 &&
                        errorResponse.response?.data?.description === 'invalid_synchronization_status'
                    ) {
                        enqueueSnackbar(providerConnection.name, {
                            variant: 'providerConnectionError',
                            key: `${providerConnection.id}-error`,
                        });
                    }
                });
        },
        [closeSnackbar, enqueueSnackbar, eventDispatcher, promptVerification],
    );

    useEffect(() => {
        if (!providersConnections.length || hasBeenChecked.current) return;

        hasBeenChecked.current = true;
        providersConnections.forEach((providerConnection) => {
            if (providerConnection.status === PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING) {
                enqueueSnackbar(providerConnection.name, {
                    variant: 'providerConnectionSynchronize',
                    key: `${providerConnection.id}-synchronizing`,
                    persist: true,
                    icon: providerConnection.logo_url,
                });
                startPolling(providerConnection.id, () =>
                    checkSynchronization({ providerConnection, product_id: null }),
                );
                return;
            }

            if (providerConnection.status === PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR_OTP) {
                promptVerification({ providerConnection, statusMessage: providerConnection.status_message });
                return;
            }

            if (providerConnection.status === PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR) {
                enqueueSnackbar(providerConnection.name, { variant: 'providerConnectionError' });
                return;
            }
        });
    }, [providersConnections, enqueueSnackbar, checkSynchronization, promptVerification, startPolling]);

    const contextValue = useMemo(() => ({ synchronize: startSynchronization }), [startSynchronization]);

    return <SyncContext.Provider value={contextValue}>{children}</SyncContext.Provider>;
}

SyncProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
